import CalendarList from "@/components/shared/CalendarList.tsx";
import { AppointmentObject, FileAttachment } from "@/@types/activities.ts";
import { useAppointmentDate } from "@/components/appointments/details/AppointmentDate.tsx";
import formatDate from "@/helpers/formatDate.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faLocationDot,
} from "@fortawesome/pro-regular-svg-icons";
import FoldableSection from "@/components/shared/FoldableSection.tsx";
import AppointmentLocationMap from "@/components/appointments/details/AppointmentLocationMap.tsx";
import { pick, values } from "lodash";
import { getLocationLinkText } from "@/components/appointments/details/AppointmentLocation.tsx";
import TruncateableContent from "@/components/activitystreams/activity/TruncateableContent.tsx";
import FileAttachments from "@/components/activitystreams/activity/object/attachments/FileAttachments.tsx";

type ActivityAppointmentArgs = AppointmentObject & {
  showMoreButton?: boolean;
  image?: {
    download_url: string;
  };
};
export default function ActivityAppointment({
  id,
  name,
  description,
  calendars,
  date,
  event_image,
  image,
  location,
  event_files,
  showMoreButton,
}: ActivityAppointmentArgs) {
  const { appointmentStart, appointmentEnd } = useAppointmentDate(date);

  return (
    <div className="activity-appointment">
      {calendars && <CalendarList className="mb-4" calendars={calendars} />}
      <div className="bg-primary/10 border-primary/25 border rounded p-2 text-sm mb-4">
        <p className="text-base mb-2">
          <a href={`/appointments/${id}`}>
            <strong>{name}</strong>
          </a>
        </p>
        <div className="flex gap-2 items-center mb-2">
          <FontAwesomeIcon icon={faCalendarDay} size="lg" />
          <span className="font-medium">
            <time dateTime={appointmentStart.toISOString()}>
              {formatDate(appointmentStart, date.all_day ? "PP" : "PP HH:mm")}
            </time>
            {/* Mehrtägige Termine wiederholen das Datum am Ende, damit die volle
                Dauer sichtbar ist (#14); am selben Tag genügt die Uhrzeit, ein
                eintägiger Ganztagstermin braucht gar kein Ende. */}
            {appointmentEnd && (!date.all_day || !date.same_day) && (
              <time dateTime={appointmentEnd.toISOString()}>
                {" - " +
                  formatDate(
                    appointmentEnd,
                    date.same_day ? "HH:mm" : date.all_day ? "PP" : "PP HH:mm",
                  )}
              </time>
            )}
          </span>
          {date.time_zone &&
            !date.all_day &&
            `(${formatDate(appointmentStart, "z")} UTC${formatDate(
              appointmentStart,
              "xxxxx",
            )})`}
        </div>
        <div className="flex gap-2 items-start w-full">
          <FontAwesomeIcon icon={faLocationDot} size="lg" className="mt-0.25" />
          <div className="flex flex-col gap-0.5">
            {location.name && (
              <span className="font-medium">{location.name}</span>
            )}
            {location.address1 && <span>{location.address1}</span>}
            {location.address2 && <span>{location.address2}</span>}
            {(location.zip || location.city) && (
              <span className="flex gap-1">
                {location.zip && <span>{location.zip}</span>}
                {location.city && <span>{location.city}</span>}
              </span>
            )}
            {location.country && <span>{location.country}</span>}
            {location.link && (
              <span>
                <a
                  href={location.link}
                  target="_blank"
                  rel="nofollow noreferrer"
                >
                  {getLocationLinkText(location)}
                </a>
              </span>
            )}
          </div>
        </div>
        {location.maps_url && (
          <div className="flex flex-col -mx-2 mt-4 -mb-2">
            <FoldableSection
              title={I18n.t("js.activitystreams.appointment.location_map.show")}
              className="w-full mb-0"
              closeTitle={I18n.t(
                "js.activitystreams.appointment.location_map.hide",
              )}
              borderBottom={false}
              borderTop
            >
              <div className="m-2 border rounded overflow-hidden">
                <AppointmentLocationMap
                  url={location.maps_url}
                  address={values(
                    pick(location, [
                      "address1",
                      "address2",
                      "zip",
                      "city",
                      "country",
                    ]),
                  ).join(" ")}
                />
              </div>
            </FoldableSection>
          </div>
        )}
      </div>
      <div>
        {(event_image?.id || image?.download_url) && (
          <div className="max-h-40 min-w-32 max-w-[25%] float-left mr-2">
            <img
              className="rounded"
              alt={name}
              src={
                event_image
                  ? `/api/storage/images/${event_image?.id}/get/200x200`
                  : image
                    ? `${image.download_url}/200x200`
                    : undefined
              }
            />
          </div>
        )}
        <TruncateableContent
          content={description}
          className="mb-2 markdown-content prose break-words"
          showMoreButton={showMoreButton}
        />
      </div>
      {event_files && (
        <div className="activity-attachments mt-4 pt-4 border-t border-neutral border-dashed">
          <FileAttachments files={event_files as FileAttachment[]} />
        </div>
      )}
    </div>
  );
}
